// The saved report as a dashboard. The summary card on top is the whole sample at a glance. The filter row scopes the tabs
// below it to a platform or market-evidence slice; their charts come from `dash`, which the server recomputes from the
// stored labels (no model call). The insights and actions always describe the whole sample. Every chart number
// counts comments and opens the evidence drawer with exactly those comments. The print page shows every section at once.
import { useEffect, useRef, useState } from 'react';
import { OPINION, PLATFORM_LABEL, SENT, api, compact, countryName, fmtDate, enoughOf, intentLabel, nf, opinions, pct, plural, useApi } from '../lib.jsx';
import { Drawer, PlatformDot } from '../ui.jsx';
import { CAT, Card, Count, Donut, Kpi, Legend, MixBars, ORD, OTHER, Quadrant, RankBars, SentTable, ShareTable, StackBar, TimeColumns, byTotal, sentSegs, signed } from '../charts.jsx';
import { Bubble, Caption, Thumb } from './Run.jsx';
import { AdsPage } from './Ads.jsx';
import { CreatorsPage } from './Creators.jsx';
import { LinkedInPage, hasSpace } from './LinkedIn.jsx';

// Three analyses, each its own page; the sentiment analysis has the tabs. The open one lives in the URL hash with the tab.
const TABS = [['overview', 'Overview'], ['sentiment', 'Sentiment'], ['themes', 'Themes'], ['keywords', 'Keywords'], ['posts', 'Posts & creators'], ['coverage', 'Coverage']];
const SEGMENTS = [['sentiment', 'Sentiment analysis', 'What people say'], ['trends', 'Trend analysis', 'What changed'], ['ads', 'Ad analysis', 'What they advertise'], ['creators', 'Creators', 'Who to work with'], ['linkedin', 'LinkedIn', 'What the space posts']];
const segOf = (tab) => (['trends', 'ads', 'creators', 'linkedin'].includes(tab) ? tab : 'sentiment');
const MAX_SECTIONS = 4;
const ALL = { platform: 'all', market: 'all' };
// Below this many comments a share chart says so and shows its table instead.
const MIN_CHART = 5;
/** Why no share or finding is stated: 7 of 7 is not "100% positive". */
const thinText = (e, name) => `Not enough relevant evidence about ${name} to draw conclusions: ${plural(e.views, 'comment')} give${e.views === 1 ? 's' : ''} a view, from ${plural(e.posts, 'post')}. A share needs at least ${e.minViews} from ${e.minPosts} or more posts.`;
const tooFew = (n) => (n >= MIN_CHART ? null : n ? `Too few analysed comments for this chart (n = ${n}).` : 'No analysed comments in this slice.');
const VOICE_LABEL = { audience: 'Audience', creator: 'The post’s creator', brand: 'The brand' };
// Fixed slots per reason; "not classified" (usually absent) takes the gray, so the usual ring is a validated one.
const LEVEL = { visual: ['Video frames analysed', ORD[2]], transcript: ['Speech transcript', ORD[1]], caption: ['Caption only', ORD[0]] };
const TIER_COLOR = { confirmed: ORD[2], likely: ORD[1], unknown: 'var(--m-unknown)', other_market: CAT[1] };
const GONE = {
  expired: 'the video link had expired', too_large: 'the video was too large to process', blocked: 'the platform blocked the download', no_media: 'no video file was available',
  download_failed: 'the download failed', ffmpeg_failed: 'frames could not be read from the video', budget: 'the video budget for this run was used up', not_video: 'the post is not a video',
};
const gone = (k) => GONE[k] ?? String(k).replace(/_/g, ' ');

/**
 * `base` is /api/runs/<id> for a workspace report, /api/admin/runs/<id> for the admin portal's view of one, or /api/demo for
 * the public demo. `print` is the report's print page. `readOnly`: no feedback, no flags, no translation.
 */
export function Report({ base, print, version, admin, readOnly }) {
  const [filter, setFilter] = useState(ALL);
  const [tab0, setTab] = useTab();
  const [picked, setPicked] = useState(null); // sections chosen on this page, until the report is read again
  const [drawer, setDrawer] = useState(null); // { title, key } | { title, ids }
  const [tid, setTid] = useState(null);
  const qs = new URLSearchParams({ ...(version ? { version } : {}), ...(filter.platform !== 'all' ? { platform: filter.platform } : {}), ...(filter.market !== 'all' ? { market: filter.market } : {}) }).toString();
  const { data: r, error } = useApi(`${base}/report${qs ? `?${qs}` : ''}`);
  if (error) return <div className="error-box">{error.message}</div>;
  if (!r) return <p className="muted">Loading report…</p>;
  if (!r.dash) return <div className="error-box">This report needs a newer page. Reload to see it.</div>;
  const d = r.dash, targets = r.scope.targets;
  const t = d.targets[tid ?? targets[0].id];
  const open = (title, q) => setDrawer({ title, ...q });
  // The previous slice stays on screen, dimmed, until the new one arrives.
  const stale = (r.filter?.platform ?? 'all') !== filter.platform || (r.filter?.market ?? 'all') !== filter.market;
  const printHref = `${print ?? (base === '/api/demo' ? '/demo/print' : `${base.replace('/api/runs/', '/reports/')}/print`)}${qs ? `?${qs}` : ''}`;
  // The analyses this report has, and those its workspace chose to show (all when none was chosen). A hidden one's link
  // opens the first shown.
  const avail = SEGMENTS.map(([k]) => k).filter((k) => k !== 'linkedin' || hasSpace(r));
  const sections = picked ?? r.sections ?? null;
  const chosen = avail.filter((k) => !sections || sections.includes(k)), visible = chosen.length ? chosen : avail;
  const tab = visible.includes(segOf(tab0)) ? tab0 : visible[0] === 'sentiment' ? 'overview' : visible[0];
  const choose = (ks) => { const was = picked; setPicked(ks); api(`${base}/sections`, { method: 'POST', body: { sections: ks } }).catch(() => setPicked(was)); };
  const Panel = { overview: Overview, sentiment: SentimentTab, trends: TrendsTab, themes: ThemesTab, keywords: KeywordsTab, posts: PostsTab, coverage: CoverageTab }[tab] ?? Overview;
  const seg = segOf(tab);

  return (
    <div className="report">
      {r.synthetic ? <div className="demo-banner">This report was made from demo data. The comments are synthetic, so the findings only show how the product works.</div> : null}
      {readOnly || base === '/api/demo' || avail.length < 2 ? null : <SectionPicker avail={avail} shown={visible} onChange={choose} />}
      <div className="report-shell">
        <SegmentNav seg={seg} setTab={setTab} tab={tab} visible={visible} />
        <div className="report-body">
          {seg === 'linkedin' ? <LinkedInPage r={r} /> : seg === 'ads' ? <AdsPage base={base} readOnly={readOnly || admin} open={open} /> : seg === 'creators' ? <CreatorsPage base={base} readOnly={readOnly || admin} open={open} /> : <>
            {seg === 'sentiment' ? <Glance r={r} open={open} /> : null}
            <FilterBar d={d} filter={filter} setFilter={setFilter} targets={targets} t={t} setTid={setTid} printHref={printHref}
              xlsxHref={base === '/api/demo' ? null : `${base}/export.xlsx?${new URLSearchParams({ ...(version ? { version } : {}), ...(filter.platform !== 'all' ? { platform: filter.platform } : {}), ...(filter.market !== 'all' ? { market: filter.market } : {}) })}`}
              csvHref={base === '/api/demo' ? null : `${base}/export.csv?${new URLSearchParams({ ...(version ? { version } : {}), ...(filter.platform !== 'all' ? { platform: filter.platform } : {}), ...(filter.market !== 'all' ? { market: filter.market } : {}), ...(targets.length > 1 ? { target: t.id } : {}) })}`} />
            {seg === 'sentiment' ? <Tabs tab={tab} setTab={setTab} /> : <h2 className="seg-title">What changed over the research’s dates</h2>}
            <div className={`tabpanel${stale ? ' stale' : ''}`} role={seg === 'sentiment' ? 'tabpanel' : undefined} id={`panel-${tab}`} aria-labelledby={seg === 'sentiment' ? `tab-${tab}` : undefined} aria-busy={stale} tabIndex={0}>
              <Panel r={r} d={d} t={t} open={open} compare={targets.length > 1} setTab={setTab} admin={admin} />
            </div>
          </>}
          {readOnly ? null : <Feedback base={base} />}
        </div>
      </div>
      {drawer ? <EvidenceDrawer key={drawer.key ?? drawer.ids.join()} base={base} version={r.version} q={drawer} onClose={() => setDrawer(null)} targets={targets} readOnly={readOnly} /> : null}
    </div>
  );
}

/** The open tab lives in the URL hash, so a link or a reload opens the same view. */
function useTab() {
  const read = () => ([...TABS, ...SEGMENTS].some(([k]) => location.hash === `#${k}`) ? location.hash.slice(1) : 'overview');
  const [tab, setTab] = useState(read);
  useEffect(() => {
    const on = () => setTab(read());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return [tab, (k) => { history.replaceState(null, '', `${location.pathname}${location.search}#${k}`); setTab(k); }];
}

/**
 * The three analyses, beside the report on a wide screen and along the bottom of a narrow one. Going back to the
 * sentiment analysis reopens the tab that was open there.
 */
function SegmentNav({ seg, tab, setTab, visible }) {
  const last = useRef('overview');
  if (seg === 'sentiment') last.current = tab;
  return (
    <nav className="seg-nav" aria-label="Analyses">
      {SEGMENTS.filter(([k]) => visible.includes(k)).map(([k, label, sub]) => (
        <button key={k} type="button" aria-current={seg === k ? 'page' : undefined} onClick={() => { setTab(k === 'sentiment' ? last.current : k); window.scrollTo({ top: 0 }); }}>
          <SegIcon k={k} /><span><b>{label}</b><small>{sub}</small></span>
        </button>
      ))}
    </nav>
  );
}
/** Which analyses the report shows: up to four, at least one, saved with the report for everyone in the workspace. */
function SectionPicker({ avail, shown, onChange }) {
  const toggle = (k) => onChange(avail.filter((x) => (x === k ? !shown.includes(k) : shown.includes(x))));
  return (
    <div className="report-tools">
      <details className="menu right">
        <summary className="btn small">Sections · {shown.length} of {avail.length}</summary>
        <fieldset className="menu-list section-list">
          <legend className="small muted">Show in this report (up to {MAX_SECTIONS})</legend>
          {SEGMENTS.filter(([k]) => avail.includes(k)).map(([k, label, sub]) => {
            const on = shown.includes(k);
            return (
              <label key={k} className="check">
                <input type="checkbox" checked={on} disabled={on ? shown.length === 1 : shown.length >= MAX_SECTIONS} onChange={() => toggle(k)} />
                <span><b>{label}</b> <span className="muted">{sub}</span></span>
              </label>
            );
          })}
          <p className="hint">Saved with this report for everyone in your workspace. Hidden sections are kept, not deleted.</p>
        </fieldset>
      </details>
    </div>
  );
}
const SEG_PATH = { sentiment: 'M4 5h16v10H9l-5 4zM8 9h8M8 12h5', trends: 'M3 17l6-6 4 4 8-8M15 7h6v6', ads: 'M3 10v4a1 1 0 0 0 1 1h3l6 4V5L7 9H4a1 1 0 0 0-1 1zM17 9a4 4 0 0 1 0 6', linkedin: 'M3 8h18v12H3zM8 8V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v3M3 13h18', creators: 'M16 20v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6M22 20v-1a4 4 0 0 0-3-3.87M16 5.13a3 3 0 0 1 0 5.75' };
const SegIcon = ({ k }) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={SEG_PATH[k]} /></svg>;

function Tabs({ tab, setTab }) {
  const keys = TABS.map(([k]) => k);
  function onKey(e) {
    const i = keys.indexOf(tab), n = keys.length;
    const j = { ArrowRight: (i + 1) % n, ArrowLeft: (i + n - 1) % n, Home: 0, End: n - 1 }[e.key];
    if (j === undefined) return;
    e.preventDefault();
    setTab(keys[j]);
    document.getElementById(`tab-${keys[j]}`)?.focus();
  }
  return (
    <div className="dash-tabs" role="tablist" aria-label="Report sections" onKeyDown={onKey}>
      {TABS.map(([k, l]) => (
        <button key={k} id={`tab-${k}`} type="button" role="tab" aria-selected={tab === k} aria-controls={`panel-${k}`} tabIndex={tab === k ? 0 : -1} onClick={() => setTab(k)}>{l}</button>
      ))}
    </div>
  );
}

/** The whole sample at a glance (the saved snapshot, whatever the filter): each target's sentiment bar with its legend. */
function Glance({ r, open }) {
  const c = r.coverage, compare = r.scope.targets.length > 1;
  return (
    <div className="glance">
      <p className="eyebrow">At a glance</p>
      {r.scope.targets.map(({ id }) => {
        const t = r.targets[id], op = opinions(t.analysed, t.sentiment), e = enoughOf(t), parts = OPINION.filter((s) => t.sentiment[s.key]).sort((a, b) => t.sentiment[b.key] - t.sentiment[a.key]);
        // Neutral comments are shown, last and apart, but never in a share: they ask something or give no view.
        const neu = (t.sentiment.neutral ?? 0) + (t.sentiment.unclear ?? 0), shown = neu ? [...parts, { key: 'neutral', label: 'Neutral or can’t tell · not counted', color: 'var(--neu)', neutral: true }] : parts;
        const size = (s) => (s.neutral ? neu : t.sentiment[s.key]);
        const go = (s) => open(`${s.label} · ${t.name}`, { key: `sent:${id}:${s.neutral ? 'noview' : s.key}` });
        return (
          <div className="g-target" key={id}>
            {compare ? <div className="g-name">{t.name}</div> : null}
            {t.analysed ? (
              <>
                <div className="g-bar" aria-hidden="true">
                  {shown.map((s) => <span key={s.key} className={s.hatch ? 'hatch' : s.neutral ? 'g-neutral' : undefined} style={{ flexGrow: size(s), backgroundColor: s.color }} title={`${s.label}: ${size(s)}`} onClick={() => go(s)} />)}
                </div>
                <ul className="g-legend" aria-label={`Sentiment of the ${nf.format(t.analysed)} analysed comments about ${t.name}`}>
                  {shown.map((s) => (
                    <li key={s.key}><button type="button" onClick={() => go(s)}><i className={s.hatch ? 'hatch' : undefined} style={{ backgroundColor: s.color }} />{s.label} <b>{nf.format(size(s))}</b>{e.ok && !s.neutral ? <span> ({pct(t.sentiment[s.key] / op)})</span> : null}</button></li>
                  ))}
                </ul>
                {neu ? <p className="g-note">Neutral or can’t tell: {plural(neu, 'comment')} that ask a question, give no view, or whose view cannot be read with confidence (“price?”, “link please”, “which shade is this?”). They are shown here but do not count toward the sentiment shares{e.ok ? `, which are worked out from the ${nf.format(op)} comments that give a view` : ''}.</p> : null}
                {e.ok ? null : <p className="warn-box">{thinText(e, t.name)}</p>}
                <p className="g-sub">
                  {plural(t.analysed, 'analysed audience comment')} about {t.name}{op < t.analysed ? `, ${nf.format(op)} giving a view` : ''}
                  {t.themes[0] ? <> · top theme <button type="button" className="g-link" onClick={() => open(`${t.themes[0].name} · ${t.name}`, { key: t.themes[0].key })}>{t.themes[0].name}</button></> : null}
                </p>
              </>
            ) : <p className="g-sub">No analysed comments about {t.name}.</p>}
          </div>
        );
      })}
      <p className="g-foot">Whole sample: {nf.format(c.comments.stored)} comments on {nf.format(c.posts)} posts by {nf.format(c.creators)} creators · {r.scope.platforms.map((p) => PLATFORM_LABEL[p] ?? p).join(', ')} · {fmtDate(r.scope.since)} – {fmtDate(r.scope.until)} · report v{r.version}</p>
    </div>
  );
}

/** One row of filters, sticky under the top bar; it scopes every tab below. */
function FilterBar({ d, filter, setFilter, targets, t, setTid, printHref, csvHref, xlsxHref }) {
  const plats = d.filterOptions.platforms, m = d.filterOptions.market;
  const country = m.country ? countryName(m.country) : 'the chosen market';
  const MARKET = [['all', 'All posts'], ['evidence', `With ${country} evidence`], ['unknown', 'Location unknown']];
  const showPlat = plats.length > 1 || filter.platform !== 'all';
  const showMarket = m.available || filter.market !== 'all';
  if (!showPlat && !showMarket && targets.length < 2) return null;
  const chips = [
    filter.platform !== 'all' ? [`Platform: ${PLATFORM_LABEL[filter.platform] ?? filter.platform}`, { platform: 'all' }] : null,
    filter.market !== 'all' ? [`Market: ${MARKET.find(([k]) => k === filter.market)?.[1]}`, { market: 'all' }] : null,
  ].filter(Boolean);
  return (
    <div className="filterbar" role="region" aria-label="Filters">
      {showPlat ? <Seg label="Platform" value={filter.platform} options={[['all', 'All'], ...plats.map((p) => [p.id, PLATFORM_LABEL[p.id] ?? p.id])]} onChange={(platform) => setFilter({ ...filter, platform })} /> : null}
      {showMarket ? <Seg label="Market evidence" value={filter.market} options={MARKET} onChange={(market) => setFilter({ ...filter, market })} /> : null}
      {targets.length > 1 ? <Seg label="Showing" value={t.id} options={targets.map((x) => [x.id, x.name])} onChange={setTid} /> : null}
      {/* A comparison always shows the exports of the target on screen. */}
      {chips.length || targets.length > 1 ? (
        <div className="fb-active">
          {chips.map(([text, reset]) => <span className="chip" key={text}>{text}<button type="button" aria-label={`Remove ${text}`} onClick={() => setFilter({ ...filter, ...reset })}>×</button></span>)}
          <span className="fb-note">The insights and actions describe the whole sample. <a href={printHref} target="_blank" rel="noopener">Print this view</a>{xlsxHref ? <> · <a href={xlsxHref}>Excel of this view</a></> : null}{csvHref ? <> · <a href={csvHref}>CSV of this view</a></> : null}</span>
        </div>
      ) : null}
    </div>
  );
}

function Seg({ label, value, options, onChange }) {
  return (
    <div className="fb-group">
      <span className="fb-label">{label}</span>
      <div className="seg" role="group" aria-label={label}>
        {options.map(([k, l]) => <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)}>{l}</button>)}
      </div>
    </div>
  );
}

// ---------- tabs ----------

// Two columns that stack on their own, so a long summary never leaves a stretched, half-empty card beside it; on narrow
// screens the columns dissolve and each card's `order` puts it back in reading order.
function Overview({ r, d, t, open, setTab, compare }) {
  const of = compare ? ` about ${t.name}` : '';
  return (
    <div className="dgrid">
      <Kpis t={t} open={open} />
      <div className="ov-col span-8">
        <Card title={`Insights${of}`} denom={`What the audience says${of} and what it suggests, for the whole sample. Each finding links to its comments.`} order={1}>
          <Findings r={r} t={t} open={open} />
        </Card>
        <ThemesCard t={t} open={open} limit={5} more={() => setTab('themes')} order={3} />
        <TimeCard r={r} d={d} t={t} open={open} order={7} />
      </div>
      <div className="ov-col span-4">
        <FeelCard t={t} open={open} order={2} />
        <IntentCard t={t} open={open} order={4} />
        <AsksCard t={t} open={open} order={4} />
        <PlatformCard t={t} open={open} order={5} />
        <KeywordsCard d={d} t={t} open={open} limit={10} more={() => setTab('keywords')} order={6} />
      </div>
      <Card span={12} title={`Actions to test${compare ? ` for ${t.name}` : ''}`} denom="Ideas to test, not predictions; none promise a sales effect. Written for the whole sample." order={8}>
        <Actions r={r} t={t} open={open} />
      </Card>
    </div>
  );
}

function SentimentTab({ r, d, t, open, compare }) {
  const all = r.scope.targets.map((x) => d.targets[x.id]);
  return (
    <div className="dgrid">
      {compare ? all.map((x) => <FeelCard key={x.id} span={6} t={x} open={open} />) : <FeelCard span={6} t={t} open={open} />}
      {compare ? <ShareCard targets={all} open={open} /> : null}
      <VoiceCard span={6} t={t} open={open} />
      {compare ? <ExclusionsCard span={6} d={d} open={open} /> : null}
      <PlatformCard span={6} t={t} open={open} />
      {compare ? <ConcentrationCard span={6} t={t} open={open} /> : <ExclusionsCard span={6} d={d} open={open} />}
      <TimeCard span={12} r={r} d={d} t={t} open={open} />
      {compare ? null : <ConcentrationCard span={12} t={t} open={open} />}
      <ThreadPostsCard tp={r.targets[t.id]?.threadPosts} name={t.name} />
    </div>
  );
}

// What changed over the dates (dash trends): the dated comments in an earlier and a recent half of similar size, shares
// compared, a change named only beyond sampling noise, and the post behind it when one post holds half or more of it.
const HALF = ['Earlier', 'Recent'];
const CHANGE = { new: ['New', 'tag warn'], rising: ['↑ Rising', 'tag'], fading: ['↓ Fading', 'tag'], steady: ['No clear change', 'muted small'] };
export const halfSpan = (h) => (h.from === h.to ? fmtDate(h.from) : `${fmtDate(h.from)} – ${fmtDate(h.to)}`);
// From the rounded shares, so "11% → 13%" reads "+2 pts".
const pts = (a, b) => { const n = Math.round(b * 100) - Math.round(a * 100); return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n)} pts`; };
/** The sentiment shift in one sentence; the print page uses it too. */
export function shiftText(shift) {
  const moved = ['positive', 'negative'].filter((s) => shift[s].change !== 'steady');
  const line = (s) => `${s} ${pct(shift[s].from)} → ${pct(shift[s].to)}`;
  return moved.length
    ? `${moved.map((s) => `The ${s} share ${shift[s].change} from ${pct(shift[s].from)} to ${pct(shift[s].to)}`).join('; ')}.`
    : `No clear shift: ${line('positive')}, ${line('negative')}, within what sampling alone could produce.`;
}
function PostRef({ t, id }) {
  const p = t.postsTable.find((x) => x.id === id);
  const label = `@${p?.creator ?? 'unknown'} on ${PLATFORM_LABEL[p?.platform] ?? p?.platform ?? 'a platform'}`;
  return p?.url ? <a href={p.url} target="_blank" rel="noreferrer noopener">{label}</a> : label;
}

function TrendsTab({ r, d, t, open }) {
  return (
    <div className="dgrid">
      <ShiftCard t={t} open={open} />
      <ThemeTrendCard t={t} open={open} />
      <PeaksCard d={d} t={t} open={open} />
      <TimeCard span={12} r={r} d={d} t={t} open={open} />
    </div>
  );
}

function ShiftCard({ t, open }) {
  const tr = t.trends, h = tr.halves;
  const note = !h ? (tr.dated ? `All ${plural(tr.dated, 'dated comment')} about ${t.name} fall on one day, so there is nothing to compare.` : 'No analysed comments with an exact date in this slice.')
    : tr.shift ? null : `Too few comments give a view to compare: each half needs at least ${h[0].enough.minViews} from ${h[0].enough.minPosts} or more posts (earlier ${nf.format(h[0].enough.views)} from ${plural(h[0].posts, 'post')}, recent ${nf.format(h[1].enough.views)} from ${plural(h[1].posts, 'post')}).`;
  return (
    <Card span={12} title="Is sentiment shifting?" note={note}
      denom={h ? `Analysed comments about ${t.name}, split by when they were posted into two halves of similar size: earlier, ${halfSpan(h[0])} (${nf.format(h[0].n)}), and recent, ${halfSpan(h[1])} (${nf.format(h[1].n)}). Shares are of the comments that give a view. A change is named only when it is larger than sampling alone would likely produce.` : null}>
      {h ? <>
        {/* Bars only beside a comparison: below the minimum, 1 comment would draw as a 100% bar. */}
        {tr.shift ? <Legend /> : null}
        <div className="tr-halves">{h.map((x, i) => [
          <button key={`l${i}`} type="button" className="linklike tr-label" onClick={() => open(`${HALF[i]} · ${halfSpan(x)} · ${t.name}`, { key: x.key })}>{HALF[i]} · {halfSpan(x)}</button>,
          tr.shift ? <StackBar key={`b${i}`} segs={sentSegs(x.sentiment)} label={`${HALF[i]} comments about ${t.name}`} onPick={(s) => open(`${s.label} · ${HALF[i]} · ${t.name}`, { key: s.key })} />
            : <span key={`b${i}`} className="small muted">{plural(x.n, 'comment')}, {nf.format(x.enough.views)} giving a view</span>,
        ])}</div>
        {tr.shift ? <p className="tr-verdict">{shiftText(tr.shift)}</p> : null}
        {h.map((x, i) => x.onePost ? <p key={i} className="warn-box">{pct(x.onePost.share)} of the {HALF[i].toLowerCase()} comments come from one post, <PostRef t={t} id={x.onePost.id} /> (<Count n={x.onePost.n} onPick={() => open(`One post · ${HALF[i]} · ${t.name}`, { key: x.onePost.key })} />), so that half mostly reflects one discussion.</p> : null)}
      </> : null}
    </Card>
  );
}

function ThemeTrendCard({ t, open }) {
  const [all, setAll] = useState(false);
  const tr = t.trends, h = tr.halves;
  const moved = tr.themes.filter((x) => x.change && x.change !== 'steady');
  const rows = all || !tr.ready ? tr.themes : moved;
  const note = !h || !tr.themes.length ? 'No themes with dated comments to compare.'
    : !tr.ready ? `Too few comments to name a change: each half needs at least ${h[0].enough.minViews} analysed comments from ${h[0].enough.minPosts} or more posts (earlier ${nf.format(h[0].n)} from ${plural(h[0].posts, 'post')}, recent ${nf.format(h[1].n)} from ${plural(h[1].posts, 'post')}). The shares are shown, without a verdict.`
    : moved.length ? null : 'No theme changed clearly between the halves: every difference is within what sampling alone could produce.';
  const cellOf = (x, i) => { const c = i ? x.recent : x.earlier; return <>{pct(c.share)} <span className="muted">(<Count n={c.n} onPick={() => open(`${x.name} · ${HALF[i]} · ${t.name}`, { key: c.key })} />)</span></>; };
  return (
    <Card span={12} title="Which themes are rising or fading?" note={note}
      denom={h ? `Each theme’s share of the analysed comments about ${t.name} in each half: earlier ${halfSpan(h[0])}, recent ${halfSpan(h[1])}. Rising or fading means the difference is beyond what sampling alone would likely produce; new means none in the earlier half. A comment can be in more than one theme.` : null}>
      {h && rows.length ? (
        <div className="table-wrap"><table className="data">
          <thead><tr><th>Theme</th><th className="num">Earlier</th><th className="num">Recent</th><th>Change</th></tr></thead>
          <tbody>{rows.map((x) => (
            <tr key={x.i}>
              <td>{x.name}{x.onePost ? <div className="small muted">Mostly one post: {pct(x.onePost.share)} from <PostRef t={t} id={x.onePost.id} /> (<Count n={x.onePost.n} onPick={() => open(`${x.name} · one post · ${t.name}`, { key: x.onePost.key })} />)</div> : null}</td>
              <td className="num">{cellOf(x, 0)}</td><td className="num">{cellOf(x, 1)}</td>
              <td>{x.change ? <span className={CHANGE[x.change][1]}>{CHANGE[x.change][0]}</span> : null} <span className="small muted">{pts(x.earlier.share, x.recent.share)}</span></td>
            </tr>
          ))}</tbody>
        </table></div>
      ) : null}
      {h && tr.ready && tr.themes.length > moved.length ? <button type="button" className="linklike small" onClick={() => setAll(!all)}>{all ? 'Show only changes' : `Show themes with no clear change (${tr.themes.length - moved.length})`}</button> : null}
    </Card>
  );
}

function PeaksCard({ d, t, open }) {
  const tr = t.trends, per = d.bucket === 'week' ? 'week' : 'day';
  const when = (b) => `${per === 'week' ? 'Week of ' : ''}${fmtDate(b.d)}`;
  return (
    <Card span={12} title="What drove the busiest periods?"
      denom={`${per === 'week' ? 'Weeks' : 'Days'} with at least 10 analysed comments about ${t.name} and twice the typical ${per} (${nf.format(tr.median)}). Collection is capped, so a peak shows where the collected comments cluster, not total conversation volume.`}
      note={tr.peaks.length ? null : `No ${per} stands out: none has 10 or more comments and twice the typical ${per}.`}>
      {tr.peaks.length ? <><Legend /><ul className="plain-list tr-peaks">{tr.peaks.map((b) => (
        <li key={b.d}>
          <p><b>{when(b)}</b>: <Count n={b.n} onPick={() => open(`${when(b)} · ${t.name}`, { key: b.key })} /> comments, {b.times.toFixed(1)}× a typical {per}.{' '}
            {b.post ? <>{pct(b.post.share)} on one post, <PostRef t={t} id={b.post.id} /> (<Count n={b.post.n} onPick={() => open(`${when(b)} · one post · ${t.name}`, { key: b.post.key })} />).</> : 'Spread over several posts.'}</p>
          <StackBar segs={sentSegs(b.sentiment)} label={`${when(b)}, comments about ${t.name}`} onPick={(s) => open(`${s.label} · ${when(b)} · ${t.name}`, { key: s.key })} />
        </li>
      ))}</ul></> : null}
    </Card>
  );
}

/** Thread posts (a Reddit thread, a tweet) about the target: the author's own words, counted apart from comments. */
function ThreadPostsCard({ tp, name }) {
  if (!tp) return null;
  const segs = OPINION.map((s) => ({ k: s.key, label: s.label, color: s.color, hatch: s.hatch, n: tp.sentiment[s.key] ?? 0 }));
  return (
    <Card span={12} title={`What do thread posts say about ${name}?`} denom={`${plural(tp.analysed, 'thread post')} (Reddit, X, Threads, Facebook) whose author speaks about ${name}, of ${nf.format(tp.labelled)} labelled. Counted apart from comments, for the whole sample, and never added to comment shares.`}
      note={tp.analysed ? null : `No thread post speaks about ${name}.`}>
      {tp.analysed ? <>
        <Legend /><StackBar segs={segs} label={`Thread posts about ${name}`} />
        <ul className="plain-list small tposts-list">{tp.items.map((x) => (
          <li key={x.id}><span className={`tag ${SENT_TAG[x.sentiment] ?? ''}`}>{SENT.find((s) => s.key === x.sentiment)?.label ?? x.sentiment}</span> {PLATFORM_LABEL[x.platform] ?? x.platform}: {x.text}{x.url ? <> · <a href={x.url} target="_blank" rel="noreferrer noopener">Open</a></> : null}</li>
        ))}</ul>
        {tp.analysed < 10 ? <p className="small muted">Too few to read as shares: read the posts.</p> : null}
      </> : null}
    </Card>
  );
}

function ThemesTab({ t, open }) {
  return (
    <div className="dgrid">
      <ThemesCard span={12} t={t} open={open} full />
      <QuadrantCard span={12} t={t} open={open} />
      <AsksCard span={12} t={t} open={open} />
    </div>
  );
}

function KeywordsTab({ d, t, open }) {
  const [scope, setScope] = useState('about');
  const [ctx, setCtx] = useState(false);
  const [noBrand, setNoBrand] = useState(false);
  const about = !!t.keywords && scope === 'about';
  return (
    <div className="dgrid">
      <KeywordsCard span={12} d={d} t={t} open={open} all={!about} ctx={!about && ctx} noBrand={noBrand} controls={
        <div className="filters">
          {t.keywords ? <Seg label="Words from" value={scope} options={[['about', `Comments about ${t.name}`], ['all', 'All conversation under the posts']]} onChange={setScope} /> : null}
          {!about ? <label className="check"><input type="checkbox" checked={ctx} onChange={(e) => setCtx(e.target.checked)} /> Include captions and subtitles</label> : null}
          <label className="check"><input type="checkbox" checked={noBrand} onChange={(e) => setNoBrand(e.target.checked)} /> Hide brand names</label>
        </div>
      } />
    </div>
  );
}

const ORIGIN = { brand: 'Brand’s own account', other_brand: 'The other brand’s account', other: 'Other accounts', unknown: 'Account unknown' };
const RELATION = { sponsored: 'Disclosed as paid or sponsored', collaboration: 'Collaboration or co-authored', affiliate_or_gifted: 'Affiliate, discount code or gifted', not_disclosed: 'Nothing disclosed' };

/** Who posted is one question, what the post discloses about payment another: "other accounts" includes paid creators. */
function OriginCard({ t }) {
  if (!t.origin) return null;
  const list = (counts, labels) => Object.keys(labels).filter((k) => counts[k]).map((k) => <li key={k}>{labels[k]} <b>{nf.format(counts[k])}</b></li>);
  return (
    <Card span={12} title="Who posted, and what did they disclose?" denom={`The posts behind the analysed comments about ${t.name}, each counted once. “Other accounts” covers creators, consumers, shops and media, paid or not.`}>
      <div className="origin-grid">
        <div><h3 className="small">Account</h3><ul className="plain-list">{list(t.origin, ORIGIN)}</ul></div>
        <div><h3 className="small">Commercial relationship</h3><ul className="plain-list">{list(t.relationship, RELATION)}</ul>
          <p className="small muted">From the platform’s own flags and disclosures in the caption. Nothing disclosed does not mean unpaid.</p></div>
      </div>
    </Card>
  );
}

function PostsTab({ t, open }) {
  return (
    <div className="dgrid">
      <OriginCard t={t} />
      <Card span={12} title={`Which posts drew the most comments about ${t.name}?`} denom="Top posts by analysed comments. Likes, comments and views are the platform’s own figures at collection; Instagram can hide likes."
        note={t.topPosts.length ? null : 'No analysed comments in this slice.'}>
        {t.topPosts.length ? <div className="tposts">{t.topPosts.map((p) => <TopPost key={p.id} p={p} t={t} open={open} />)}</div> : null}
      </Card>
      <PostsTableCard span={12} t={t} open={open} />
      <CreatorsCard span={12} t={t} open={open} />
    </div>
  );
}

/** Every post behind the counted comments: the platform's own figures, what was collected and what counts. */
function PostsTableCard({ t, open, span }) {
  const [all, setAll] = useState(false);
  const rows = t.postsTable ?? [];
  if (!rows.length) return null;
  const shown = all ? rows : rows.slice(0, 10);
  const fig = (v) => (v === null || v === undefined ? '–' : compact(v));
  return (
    <Card span={span} title={`Every post behind the comments about ${t.name}`} denom={`${plural(rows.length, 'post')}, most counted comments first. Views and comments are the platform’s own figures at collection; collected is what was read (capped per post); counted is what passed both checks and is about ${t.name}.`}>
      <div className="table-wrap"><table className="data posts-table">
        <thead><tr><th>Post</th><th className="num">Views</th><th className="num">Comments on platform</th><th className="num">Collected</th><th className="num">Counted</th><th className="num">Share</th>{['positive', 'negative'].map((s) => <th className="num" key={s}>{SENT.find((y) => y.key === s).label}</th>)}</tr></thead>
        <tbody>{shown.map((p) => (
          <tr key={p.id}>
            <td><PlatformDot p={p.platform} /> {p.url ? <a href={p.url} target="_blank" rel="noreferrer noopener">{p.caption || 'No caption'}</a> : p.caption || 'No caption'}</td>
            <td className="num">{fig(p.views)}</td><td className="num">{fig(p.onPlatform)}</td><td className="num">{nf.format(p.collected)}</td>
            <td className="num"><Count n={p.n} onPick={() => open(`Post by @${p.creator ?? 'unknown'} · ${t.name}`, { key: p.key })} /></td>
            <td className="num">{pct(p.n / t.analysed.n)}</td>
            {['positive', 'negative'].map((s) => <td className="num" key={s}><Count n={p.sentiment[s].n} onPick={() => open(`${SENT.find((y) => y.key === s).label} · post by @${p.creator ?? 'unknown'}`, { key: p.sentiment[s].key })} /></td>)}
          </tr>
        ))}</tbody>
      </table></div>
      {rows.length > 10 ? <button type="button" className="linklike small" onClick={() => setAll(!all)}>{all ? 'Show the first 10' : `Show all ${rows.length} posts`}</button> : null}
    </Card>
  );
}

function CoverageTab({ r, d, open, admin }) {
  return (
    <div className="dgrid">
      <CollectionCard span={12} r={r} d={d} />
      {r.coverage.ratings ? <RatingsCard span={12} x={r.coverage.ratings} /> : null}
      <CommentsCard span={6} d={d} open={open} />
      <ExclusionsCard span={6} d={d} open={open} />
      {d.market ? <MarketCard span={6} d={d} open={open} targets={r.scope.targets} /> : null}
      <ContextCard span={d.market ? 6 : 12} r={r} d={d} open={open} />
      <PublishedCard span={12} r={r} d={d} open={open} />
      <MethodCard span={12} r={r} admin={admin} />
    </div>
  );
}

// ---------- cards ----------

function Kpis({ t, open }) {
  const a = t.analysed.n, s = t.sentiment, th = t.themes[0], c = t.concentration, op = opinions(a, s);
  const few = !enoughOf(t).ok;
  const share = (n, of = op) => (of ? pct(n / of) : '–');
  const go = (title, key) => (a ? () => open(`${title} · ${t.name}`, { key }) : null);
  return (
    <div className="kpis span-12" role="group" aria-label={`Key numbers for ${t.name}`}>
      <Kpi label="Analysed comments" value={nf.format(a)} sub={`audience comments about ${t.name}`} onClick={go('Analysed comments', t.analysed.key)} />
      <Kpi label="Positive" value={few ? nf.format(s.positive.n) : share(s.positive.n)} sub={few ? `of ${nf.format(op)} with a view: not enough for a share` : `${plural(s.positive.n, 'comment')}, of ${nf.format(op)} with a view`} onClick={s.positive.n ? go('Positive', s.positive.key) : null} />
      <Kpi label="Negative" value={few ? nf.format(s.negative.n) : share(s.negative.n)} sub={few ? `of ${nf.format(op)} with a view: not enough for a share` : `${plural(s.negative.n, 'comment')}, of ${nf.format(op)} with a view`} onClick={s.negative.n ? go('Negative', s.negative.key) : null} />
      <Kpi label="Net sentiment" value={few ? '–' : signed(t.net.value)} sub={few ? 'too few comments to score' : `−100 to +100, from ${nf.format(t.net.n)} positive or negative`} />
      <Kpi label="Posts and creators" value={nf.format(c.posts)} sub={`posts by ${nf.format(c.creators)} creator${c.creators === 1 ? '' : 's'}, behind these comments`} />
      <Kpi label="Top theme" text value={th ? th.name : '–'} sub={th ? `${plural(th.n, 'comment')} (${share(th.n, a)})` : 'no themes yet'} onClick={th ? go(th.name, th.key) : null} />
    </div>
  );
}

function FeelCard({ t, open, span = 12, order }) {
  const a = opinions(t.analysed.n, t.sentiment), items = sentSegs(t.sentiment), none = t.noView ?? { n: t.sentiment.neutral.n + (t.sentiment.unclear?.n ?? 0), key: t.sentiment.neutral.key }, e = enoughOf(t);
  const pick = (s) => open(`${s.label} · ${t.name}`, { key: s.key });
  const note = tooFew(a) ?? (e.ok ? null : thinText(e, t.name));
  return (
    <Card span={span} order={order} title={`How do people feel about ${t.name}?`} denom={`From ${plural(a, 'analysed audience comment')} that give a view.`}
      note={note} table={a ? <ShareTable head="Sentiment" rows={items} onPick={pick} counts={!e.ok} /> : null}
      foot={none.n ? <div className="card-f"><button type="button" className="linklike small" onClick={() => pick({ label: 'No opinion', key: none.key })}>{plural(none.n, 'more comment')} ask something, give no opinion or cannot be read with confidence, so {none.n === 1 ? 'it is' : 'they are'} not in these shares</button></div> : null}>
      {note ? null : <Donut items={items} label={`Sentiment toward ${t.name}`} onPick={pick} />}
    </Card>
  );
}

function ShareCard({ targets, open }) {
  const segs = targets.map((x, i) => ({ k: x.id, label: x.name, n: x.analysed.n, key: x.analysed.key, color: CAT[i] }));
  const sizes = segs.map((s) => s.n), sum = sizes.reduce((a, n) => a + n, 0);
  const unequal = Math.min(...sizes) > 0 ? Math.max(...sizes) / Math.min(...sizes) > 1.5 : true;
  const pick = (s) => open(`Analysed comments · ${s.label}`, { key: s.key });
  return (
    <Card span={12} title="Share of analysed comments in this sample" denom="Each was collected with the same allowance, so this reflects the sample, not share of voice or market size."
      table={<ShareTable head="Researching" rows={segs} onPick={pick} />}>
      <ul className="inline-legend">{segs.map((s) => <li key={s.k}><button type="button" onClick={() => pick(s)}><i style={{ background: s.color }} />{s.label} <b>{nf.format(s.n)}</b> ({sum ? pct(s.n / sum) : '–'})</button></li>)}</ul>
      <StackBar segs={segs} label="Share of analysed comments" onPick={pick} />
      {unequal ? <p className="warn-box small" style={{ marginTop: 12 }}>The samples are unequal in size, so compare shares, not counts, and treat the smaller one with more caution.</p> : null}
    </Card>
  );
}

function ThemesCard({ t, open, span = 12, order, limit, more, full }) {
  const rows = (limit ? t.themes.slice(0, limit) : t.themes).map((th) => ({
    id: th.i, label: th.name, title: th.name, n: th.n, key: th.key, sentiment: th.sentiment,
    // One discussion repeated is not many incidents: say when one thread holds most of a theme.
    sub: `${nf.format(th.posts)} post${th.posts === 1 ? '' : 's'}${th.posts > 1 && th.topPostShare >= 0.5 ? ` · ${pct(th.topPostShare)} from one thread` : ''}${full && th.description ? ` · ${th.description}` : ''}`,
  }));
  const pick = (row, s) => open(s ? `${s.label} · ${row.title} · ${t.name}` : `${row.title} · ${t.name}`, { key: s ? s.key : row.key });
  const count = limit && t.themes.length > limit ? `The top ${limit} of ${t.themes.length} themes` : `${t.themes.length} theme${t.themes.length === 1 ? '' : 's'}`;
  return (
    <Card span={span} order={order} title="What do people talk about?" denom={`${count} in ${nf.format(t.analysed.n)} analysed comments about ${t.name}, most-discussed first. A comment can be in more than one theme.`}
      note={rows.length ? null : 'No themes: too few analysed comments.'} table={rows.length ? <SentTable head="Theme" rows={rows} onPick={pick} /> : null}
      foot={<div className="card-f">
        {t.unthemed.n ? <button type="button" className="linklike small" onClick={() => open(`Not in a theme · ${t.name}`, { key: t.unthemed.key })}>{t.unthemed.n === 1 ? '1 analysed comment is' : `${nf.format(t.unthemed.n)} analysed comments are`} not in any theme</button> : <span />}
        {more && t.themes.length ? <button type="button" className="linklike small" onClick={more}>All themes and the theme map →</button> : null}
      </div>}>
      {rows.length ? <><Legend /><MixBars rows={rows} onPick={pick} /></> : null}
    </Card>
  );
}

function QuadrantCard({ t, open, span }) {
  const pts = t.themes.map((th, i) => {
    const pos = th.sentiment.positive.n, neg = th.sentiment.negative.n;
    return { id: th.i, rank: i + 1, label: th.name, n: th.n, posts: th.posts, key: th.key, pos, neg, net: pos + neg ? (pos - neg) / (pos + neg) : null };
  });
  const plotted = pts.filter((p) => p.net !== null), left = pts.filter((p) => p.net === null);
  const pick = (p) => open(`${p.label} · ${t.name}`, { key: p.key });
  return (
    <Card span={span} title="Which themes are big, and how do they feel?" denom="Right means more comments, higher means more positive than negative (net sentiment, −100 to +100), bigger means more posts. Themes overlap."
      note={plotted.length >= 2 ? null : 'Too few themes with positive or negative comments for this chart.'}
      table={pts.length ? (
        <div className="table-wrap"><table className="data">
          <thead><tr><th>#</th><th>Theme</th><th className="num">Comments</th><th className="num">Posts</th><th className="num">Positive</th><th className="num">Negative</th><th className="num">Net</th></tr></thead>
          <tbody>{pts.map((p) => <tr key={p.id}><td>{p.rank}</td><td><button type="button" className="linklike" onClick={() => pick(p)}>{p.label}</button></td><td className="num">{nf.format(p.n)}</td><td className="num">{nf.format(p.posts)}</td><td className="num">{nf.format(p.pos)}</td><td className="num">{nf.format(p.neg)}</td><td className="num">{signed(p.net)}</td></tr>)}</tbody>
        </table></div>
      ) : null}
      foot={plotted.length >= 2 ? (
        <div className="card-f q-key">
          <ol>{plotted.map((p) => <li key={p.id} value={p.rank}>{p.label}</li>)}</ol>
          {left.length ? <p className="small muted">Not plotted, as none of their comments is positive or negative: {left.map((p) => p.label).join(', ')}.</p> : null}
        </div>
      ) : null}>
      {plotted.length >= 2 ? <Quadrant points={plotted} onPick={pick} /> : null}
    </Card>
  );
}

function IntentCard({ t, open, span = 12, order }) {
  const a = t.analysed.n;
  const slot = Object.fromEntries(t.intents.donut.filter((x) => x.k !== 'rest').map((x, i) => [x.k, CAT[i]]));
  const folded = (t.intents.donut.find((x) => x.k === 'rest')?.members ?? []).filter((k) => k !== 'other').map((k) => intentLabel(k).toLowerCase());
  const items = t.intents.donut.map((x) => x.k === 'rest'
    ? { ...x, label: 'Other', color: OTHER, title: x.members.map(intentLabel).join(', ') }
    : { ...x, label: intentLabel(x.k), color: slot[x.k] });
  const pick = (x) => open(`${x.k === 'rest' ? 'Other intents' : x.label} · ${t.name}`, { key: x.key });
  return (
    <Card span={span} order={order} title="Why do people comment?" denom={`From ${plural(a, 'analysed comment')}, one intent each.${folded.length ? ` “Other” also includes ${folded.join(', ')}.` : ''}`}
      note={tooFew(a)} table={a ? <ShareTable head="Intent" rows={t.intents.list.map((x) => ({ ...x, label: intentLabel(x.k), color: slot[x.k] ?? OTHER }))} onPick={(x) => open(`${x.label} · ${t.name}`, { key: x.key })} /> : null}>
      {tooFew(a) ? null : <Donut items={items} label={`Intents of comments about ${t.name}`} onPick={pick} />}
    </Card>
  );
}

/** Questions and bare "price?" / "link please" comments, grouped by theme: not sentiment, but what buyers want to know. */
function AsksCard({ t, open, span = 12, order }) {
  const q = t.asks;
  if (!q) return null; // a dashboard from before the card
  const rows = q.themes.map((x) => ({ id: x.i, label: x.name, value: x.n, n: x.n, key: x.key }));
  // Reports read before the reply status existed have no unanswered cell.
  const noReply = q.unanswered && q.replyKnown ? ` ${nf.format(q.unanswered.n)} of the ${plural(q.replyKnown, 'question')} whose replies the platform reports had no reply when collected.` : '';
  return (
    <Card span={span} order={order} title="What do people ask?" denom={`${plural(q.n, 'question')} about ${t.name}, including bare requests for the price or a link, by theme. A question can be in more than one theme.${noReply}`}
      note={q.n ? null : `No one asked anything about ${t.name} in this slice.`}
      foot={q.n ? <div className="card-f">
        <button type="button" className="linklike small" onClick={() => open(`Questions · ${t.name}`, { key: q.key })}>Read all {plural(q.n, 'question')}</button>
        {q.unanswered?.n ? <button type="button" className="linklike small" onClick={() => open(`Questions with no reply · ${t.name}`, { key: q.unanswered.key })}>{nf.format(q.unanswered.n)} with no reply</button> : null}
        {q.unthemed.n ? <button type="button" className="linklike small" onClick={() => open(`Questions not in a theme · ${t.name}`, { key: q.unthemed.key })}>{nf.format(q.unthemed.n)} not in any theme</button> : null}
      </div> : null}>
      {rows.length ? <RankBars rows={rows} onPick={(x) => open(`Questions · ${x.label} · ${t.name}`, { key: x.key })} /> : null}
    </Card>
  );
}

function VoiceCard({ t, open, span }) {
  const items = t.voices.map((v, i) => ({ ...v, label: VOICE_LABEL[v.k] ?? v.k, color: CAT[i] }));
  const pick = (v) => open(`${v.label} · ${t.name}`, { key: v.key });
  return (
    <Card span={span} title="Who is talking?" denom={`${plural(t.about, 'comment')} labelled about ${t.name}. Only the audience’s count toward sentiment; the creator’s and the brand’s replies are left out.`}
      note={tooFew(t.about)} table={t.about ? <ShareTable head="Written by" rows={items} onPick={pick} /> : null}>
      {tooFew(t.about) ? null : <Donut items={items} label="Who wrote the comments" onPick={pick} />}
    </Card>
  );
}

function ExclusionsCard({ d, open, span }) {
  // Up to eight reasons: ranked bars with labels, not one colour per reason.
  const items = [...d.exclusions].sort((a, b) => b.n - a.n);
  const n = items.reduce((a, e) => a + e.n, 0);
  const pick = (e) => open(e.label, { key: e.key });
  return (
    <Card span={span} title="What was left out of sentiment?" denom={`${nf.format(n)} of ${nf.format(d.comments.stored.n)} stored comments are outside every sentiment count. Click a reason to check it.`}
      note={n ? (n < MIN_CHART ? `Too few comments left out for this chart (n = ${n}).` : null) : 'Nothing was left out.'} table={n ? <ShareTable head="Reason" rows={items} onPick={pick} /> : null}>
      {n >= MIN_CHART ? <RankBars rows={items.map((e) => ({ ...e, id: e.k, value: e.n }))} onPick={pick} color={OTHER} /> : null}
    </Card>
  );
}

function PlatformCard({ t, open, span = 12, order }) {
  const rows = t.byPlatform.map((x) => ({ id: x.p, label: PLATFORM_LABEL[x.p] ?? x.p, title: PLATFORM_LABEL[x.p] ?? x.p, n: x.total.n, key: x.total.key, sentiment: x.sentiment }));
  const pick = (row, s) => open(`${s ? `${s.label} · ` : ''}${row.title} · ${t.name}`, { key: s ? s.key : row.key });
  return (
    <Card span={span} order={order} title="Does it differ by platform?" denom={`Sentiment toward ${t.name} as a share of each platform’s analysed comments.`}
      note={rows.some((x) => x.n) ? null : 'No analysed comments in this slice.'} table={<SentTable head="Platform" rows={rows} onPick={pick} />}>
      {rows.some((x) => x.n) ? (
        <>
          <Legend />
          <div className="prows">
            {rows.map((x) => (
              <div className="prow" key={x.id}>
                <div className="prow-h"><PlatformDot p={x.id} /><button type="button" className="linklike small" disabled={!x.n} onClick={() => pick(x, null)}>{plural(x.n, 'comment')}</button></div>
                {x.n ? <StackBar segs={sentSegs(x.sentiment)} label={x.label} onPick={(s) => pick(x, s)} /> : <p className="small muted">No analysed comments on {x.label}.</p>}
                {x.n && x.n < enoughOf(t).minViews ? <p className="small muted">Too few to say how {x.label} feels: read the {plural(x.n, 'comment')} rather than the shares.</p> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </Card>
  );
}

/**
 * By default the words in the comments counted for the selected target (reports from 2026-10-19 on); "all" is every
 * audience comment under the collected posts, which includes talk about the creator or anything else.
 */
function KeywordsCard({ d, t, open, span = 12, order, limit, more, all: everything = false, ctx = false, noBrand = false, controls }) {
  const about = !everything && !!t.keywords;
  const view = `${about ? 'about' : ctx ? 'context' : 'audience'}${noBrand ? '_nobrand' : ''}`;
  const all = byTotal(about ? t.keywords[view] : d.keywords.views[view]);
  const list = limit ? all.slice(0, limit) : all;
  const docs = d.keywords.documents;
  const from = about ? `${nf.format(t.keywords.documents)} analysed audience comments about ${t.name}` : `all ${nf.format(docs.comments)} audience comments under the collected posts, whatever they are about${ctx ? `, and ${nf.format(docs.posts)} captions and subtitles` : ''}`;
  const rows = list.map((k) => ({ id: k.key, label: k.phrase, value: k.total, n: k.n, key: k.key, title: k.posts ? `${plural(k.n, 'comment')}, ${nf.format(k.posts)} ${k.posts === 1 ? 'caption or subtitle' : 'captions or subtitles'}` : plural(k.n, 'comment') }));
  return (
    <Card span={span} order={order} title="Which words do people use?" foot={more && all.length > (limit ?? 0) ? <div className="card-f"><span /><button type="button" className="linklike small" onClick={more}>All {all.length} words and phrases →</button></div> : null}
      denom={`${limit ? `The top ${Math.min(limit, all.length)}` : 'Words and short phrases'} from ${from}, highest count first. The number is how many ${ctx ? 'comments and posts' : 'comments'} contain it, not search volume.`}
      note={rows.length ? null : 'No phrase appears in more than one comment.'}>
      {controls}
      {rows.length ? <RankBars rows={rows} rank onPick={(k) => open(`“${k.label}”`, { key: k.key })} /> : null}
    </Card>
  );
}

function TimeCard({ r, d, t, open, span = 12, order }) {
  const buckets = t.time.map((b) => ({ ...b, parts: sentSegs(b.sentiment, SENT) }));
  const n = buckets.reduce((a, b) => a + b.n, 0);
  const per = d.bucket === 'week' ? 'week' : 'day';
  const pick = (b, s) => open(`${s ? `${s.label} · ` : ''}${per === 'week' ? 'Week of ' : ''}${fmtDate(b.d)} · ${t.name}`, { key: s ? s.key : b.key });
  return (
    <Card span={span} order={order} title="When did people comment?" denom={`Analysed comments about ${t.name} per ${per} (UTC), by when each comment was posted: not when the post appeared, not when it was collected. This is the collected sample, not total conversation volume; an empty ${per} means no collected comment, not no conversation.`}
      note={n ? null : 'No analysed comments with an exact date in this slice.'} table={n ? <SentTable head={per === 'week' ? 'Week of' : 'Day'} rows={buckets.map((b) => ({ ...b, id: b.d, label: fmtDate(b.d) }))} onPick={pick} /> : null}
      foot={t.undated.n || t.approximate?.n ? <div className="card-f small">
        {t.undated.n ? <button type="button" className="linklike small" onClick={() => open(`No date · ${t.name}`, { key: t.undated.key })}>{plural(t.undated.n, 'comment')} with no date</button> : <span />}
        {t.approximate?.n ? <button type="button" className="linklike small" onClick={() => open(`Approximate date · ${t.name}`, { key: t.approximate.key })}>{plural(t.approximate.n, 'comment')} dated only roughly (“2 weeks ago”), left out of the chart</button> : null}
      </div> : null}>
      {n ? <><Legend items={sentSegs(null, SENT)} /><TimeColumns buckets={buckets} bucket={d.bucket} from={r.scope.since} to={r.scope.until} label={`Analysed comments about ${t.name} per ${per}`} onPick={(b) => pick(b, null)} /></> : null}
    </Card>
  );
}

function ConcentrationCard({ t, open, span }) {
  // The top creator is counted across platforms; link it only when one creator row holds all of it, so the drawer matches.
  const c = t.concentration, top = t.topPosts.find((p) => p.id === c.topPostId), sp = t.spread, w = sp?.withoutTop;
  if (!sp) return null; // a report saved before the spread measures
  const creator = t.creators.find((x) => (x.handle ?? 'unknown') === c.topCreator && x.n === Math.round(c.topCreatorShare * t.analysed.n));
  return (
    <Card span={span} title="Does one post or creator dominate?" denom={`The ${plural(t.analysed.n, 'analysed comment')} about ${t.name} come from ${plural(c.posts, 'post')} by ${plural(c.creators, 'creator')}.`}>
      <div className="conc">
        {c.warn ? <p className="warn-box">One thread dominates: {pct(c.topPostShare)} of these comments come from a single post, so it weighs heavily on the picture.</p> : null}
        {c.creatorWarn ? <p className="warn-box">One creator dominates: {pct(c.topCreatorShare)} of these comments are on posts by @{c.topCreator}{c.topCreatorOfficial ? ', the brand’s own account. Its commenters are mostly existing fans, so this leans positive.' : '.'}</p> : null}
        {sp.flags.map((s) => { const x = sp.bySentiment[s], label = SENT.find((y) => y.key === s)?.label.toLowerCase() ?? s; return (
          <p className="warn-box" key={s}>{pct(x.topPostShare)} of the {label} comments ({nf.format(x.top?.n ?? 0)} of {nf.format(x.total)}) are under one post, so the {label} share rests heavily on one discussion.{' '}
            {x.top ? <button type="button" className="linklike" onClick={() => open(`${SENT.find((y) => y.key === s)?.label} · largest post · ${t.name}`, { key: x.top.key })}>Read them</button> : null}</p>); })}
        {!c.warn && !c.creatorWarn && !sp.flags.length && t.analysed.n ? <p>No single post or creator dominates.</p> : null}
        {t.analysed.n ? <p className="small">
          {plural(sp.posts, 'post')} behind these comments; a post has {sp.median === 1 ? 'one' : nf.format(sp.median)} counted comment{sp.median === 1 ? '' : 's'} at the median, and the spread is like {plural(Math.max(1, Math.round(sp.effectivePosts)), 'equal post')}.
          {sp.thin ? ` ${plural(sp.thin, 'post')} ha${sp.thin === 1 ? 's' : 've'} fewer than 5 (${plural(sp.thinComments, 'comment')} in all).` : ''}
        </p> : null}
        {w && enoughOf(t).ok ? (
          <div className="table-wrap"><table className="data">
            <thead><tr><th>Without the largest post</th>{['positive', 'negative'].map((s) => <th className="num" key={s}>{SENT.find((y) => y.key === s).label}</th>)}<th className="num">With a view</th></tr></thead>
            <tbody>
              <tr><td>All posts</td>{['positive', 'negative'].map((s) => <td className="num" key={s}>{pct(t.sentiment[s].n / opinions(t.analysed.n, t.sentiment))}</td>)}<td className="num">{nf.format(opinions(t.analysed.n, t.sentiment))}</td></tr>
              <tr><td>All but the largest (<button type="button" className="linklike" onClick={() => open(`Largest post · ${t.name}`, { key: w.largest.key })}>{plural(w.largest.n, 'comment')} left out</button>)</td>{['positive', 'negative'].map((s) => <td className="num" key={s}>{opinions(w.analysed, w.sentiment) ? pct(w.sentiment[s] / opinions(w.analysed, w.sentiment)) : '–'}</td>)}<td className="num">{nf.format(opinions(w.analysed, w.sentiment))}</td></tr>
            </tbody>
          </table></div>
        ) : null}
        <p className="small">
          {top ? <button type="button" className="linklike" onClick={() => open(`Top post · ${t.name}`, { key: top.key })}>Top post: {pct(c.topPostShare)} of comments</button> : `Top post: ${pct(c.topPostShare)} of comments`}
          {' · '}
          {creator ? <button type="button" className="linklike" onClick={() => open(`@${creator.handle ?? 'unknown'} · ${t.name}`, { key: creator.key })}>top creator: {pct(c.topCreatorShare)}</button> : `top creator: ${pct(c.topCreatorShare)}`}
        </p>
      </div>
    </Card>
  );
}

function TopPost({ p, t, open }) {
  const e = p.engagement ?? {};
  const figures = [['likes', e.likes], ['comments', e.comments], ['views', e.views], ['shares', e.shares]].filter(([, v]) => v !== null && v !== undefined);
  const who = `@${p.creator ?? 'unknown'}`;
  return (
    <article className="tpost">
      <Thumb src={p.thumbnail_url} label={PLATFORM_LABEL[p.platform]} />
      <div className="tp-main">
        <div className="qmeta small"><PlatformDot p={p.platform} /><span>{who}</span>{p.coauthors?.length ? <span>with {p.coauthors.map((c) => `@${c}`).join(', ')}</span> : null}{p.official ? <span className="tag">official account</span> : null}{p.relationship && p.relationship !== 'not_disclosed' ? <span className="tag">{RELATION[p.relationship]}</span> : null}<span>{fmtDate(p.published_at)}</span></div>
        <p className="tp-cap small">{p.caption || <span className="muted">No caption</span>}</p>
        {figures.length ? <p className="small muted">{figures.map(([k, v]) => `${compact(v)} ${k}`).join(' · ')} on the platform</p> : null}
        <div className="tp-tags">
          {p.market ? <MarketBadge m={p.market} /> : null}
          {p.context && p.context !== 'caption' ? <span className="tag">{LEVEL[p.context]?.[0] ?? p.context}</span> : null}
        </div>
        <button type="button" className="linklike small tp-n" onClick={() => open(`Post by ${who} · ${t.name}`, { key: p.key })}><b>{nf.format(p.n)}</b> analysed comment{p.n === 1 ? '' : 's'}</button>
        <StackBar segs={sentSegs(p.sentiment)} label={`Post by ${who}`} onPick={(s) => open(`${s.label} · post by ${who}`, { key: s.key })} />
        {p.url ? <a className="small" href={p.url} target="_blank" rel="noreferrer noopener">Open on {PLATFORM_LABEL[p.platform] ?? p.platform}</a> : null}
      </div>
    </article>
  );
}

function CreatorsCard({ t, open, span }) {
  const rows = t.creators;
  return (
    <Card span={span} title="Whose posts did people react to?" denom={`${rows.length < t.creatorCount ? `The top ${rows.length} of ${t.creatorCount}` : `All ${t.creatorCount}`} creators by analysed comments about ${t.name}. Followers are the platform’s figure at collection.`}
      note={rows.length ? null : 'No analysed comments in this slice.'}>
      {rows.length ? (
        <div className="table-wrap"><table className="data creators">
          <thead><tr><th>Creator</th><th>Platform</th><th className="num">Followers</th><th className="num">Posts</th><th className="num">Analysed comments</th><th className="num">Share</th><th className="mixcol">Sentiment mix</th></tr></thead>
          <tbody>{rows.map((c) => {
            const who = `@${c.handle ?? 'unknown'}`;
            return (
              <tr key={`${c.platform}:${c.handle}`}>
                <td>{who}{c.official ? <span className="tag" style={{ marginLeft: 6 }}>official</span> : null}{c.verified ? <span className="tag" style={{ marginLeft: 6 }}>verified</span> : null}</td>
                <td><PlatformDot p={c.platform} /></td>
                <td className="num">{c.followers === null ? '–' : compact(c.followers)}</td>
                <td className="num">{nf.format(c.posts)}</td>
                <td className="num"><button type="button" className="linklike" onClick={() => open(`${who} · ${t.name}`, { key: c.key })}>{nf.format(c.n)}</button></td>
                <td className="num">{pct(c.share)}</td>
                <td className="mixcol"><StackBar segs={sentSegs(c.sentiment)} label={who} onPick={(s) => open(`${s.label} · ${who}`, { key: s.key })} /></td>
              </tr>
            );
          })}</tbody>
        </table></div>
      ) : null}
    </Card>
  );
}

function CollectionCard({ r, d, span }) {
  const name = (id) => r.scope.targets.find((x) => x.id === id)?.name ?? id;
  const rows = Object.entries(r.coverage.byTargetPlatform).map(([k, v]) => ({ k, tid: k.split(':')[0], p: k.split(':')[1], ...v }))
    .filter((x) => d.filter.platform === 'all' || x.p === d.filter.platform);
  const max = Math.max(1, ...rows.map((x) => x.candidates ?? 0));
  const bar = (v, i) => <span className="fbar"><span style={{ width: `${((v ?? 0) / max) * 100}%`, background: ORD[i] }} /></span>;
  return (
    <Card span={span} title="How was the sample collected?" denom={`Posts found by the searches, judged relevant, and selected for comments${d.filter.platform !== 'all' ? `, for ${PLATFORM_LABEL[d.filter.platform] ?? d.filter.platform}` : ''}. Collection counts are not split by market evidence.`}>
      <Legend items={[{ k: 'f', label: 'Found', color: ORD[0] }, { k: 'r', label: 'Relevant', color: ORD[1] }, { k: 's', label: 'Selected', color: ORD[2] }]} />
      <div className="table-wrap"><table className="data funnel-t">
        <thead><tr><th>Researching</th><th>Platform</th><th className="num">Posts found</th><th className="num">Relevant</th><th className="num">Selected</th><th className="num">Comments collected</th><th className="num">of which replies</th></tr></thead>
        <tbody>{rows.map((x) => (
          <tr key={x.k}>
            <td>{name(x.tid)}</td><td><PlatformDot p={x.p} /></td>
            <td className="num">{bar(x.candidates, 0)}{nf.format(x.candidates ?? 0)}</td>
            <td className="num">{bar(x.relevant, 1)}{nf.format(x.relevant ?? 0)}</td>
            <td className="num">{bar(x.selected, 2)}{nf.format(x.selected ?? 0)}</td>
            <td className="num">{nf.format(x.comments ?? 0)}</td><td className="num">{nf.format(x.replies ?? 0)}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </Card>
  );
}

/** Amazon star ratings, counted in code from the reviews collected (the whole sample: filters do not apply). */
function RatingsCard({ x, span }) {
  const max = Math.max(1, ...Object.values(x.byStar));
  return (
    <Card span={span} title="What star ratings do the Amazon reviews give?" denom={`Average ${x.average} of 5 over ${plural(x.reviews, 'review')} collected, counted from those reviews, not Amazon’s overall rating. The review text is labelled on its own; stars are shown beside each review.`}>
      <div className="table-wrap"><table className="data funnel-t">
        <thead><tr><th>Stars</th><th className="num">Reviews</th><th className="num">Share</th></tr></thead>
        <tbody>{[5, 4, 3, 2, 1].map((n, i) => (
          <tr key={n}><td>{n} of 5</td><td className="num"><span className="fbar"><span style={{ width: `${(x.byStar[n] / max) * 100}%`, background: ORD[i % ORD.length] }} /></span>{nf.format(x.byStar[n])}</td><td className="num">{pct(x.byStar[n] / x.reviews)}</td></tr>
        ))}</tbody>
      </table></div>
    </Card>
  );
}

function CommentsCard({ d, open, span }) {
  const c = d.comments, max = Math.max(1, c.stored.n);
  const steps = [['Stored', c.stored, 'every comment collected in this slice'], ['Classified', c.classified, 'labelled by the model'], ['Analysed', c.analysed, 'audience comments about a target, counted in sentiment']];
  return (
    <Card span={span} title="How many comments were analysed?" denom="From every stored comment to the ones each sentiment share is based on.">
      <ol className="cfunnel">
        {steps.map(([label, cell, hint], i) => (
          <li key={label}>
            <button type="button" className="cf-row" disabled={!cell.n} onClick={() => open(`${label} comments`, { key: cell.key })}>
              <span className="cf-l"><b>{label}</b> <span className="muted">{hint}</span></span>
              <span className="cf-v">{nf.format(cell.n)}</span>
              <span className="fbar wide"><span style={{ width: `${(cell.n / max) * 100}%`, background: ORD[i] }} /></span>
            </button>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** Posts from another market are never selected, so the tiers cannot show them; the collection counts can. */
export function skippedNote(m, targets) {
  const xs = m?.skipped ?? [];
  if (!xs.length) return null;
  if (targets.length < 2) return xs[0].posts === 1 ? '1 post from another country was found and skipped; its comments were not collected.'
    : `${nf.format(xs[0].posts)} posts from other countries were found and skipped; their comments were not collected.`;
  const name = (id) => targets.find((t) => t.id === id)?.name ?? id;
  return `Posts from other countries were found and skipped, so their comments were not collected: ${xs.map((x) => `${nf.format(x.posts)} for ${name(x.t)}`).join(', ')}.`;
}

function MarketCard({ d, open, span, targets }) {
  const m = d.market, country = m.country ? countryName(m.country) : 'the chosen market';
  const LABEL = { confirmed: `Confirmed ${country}`, likely: `Likely ${country}`, unknown: 'Location unknown', other_market: 'Another market' };
  const items = m.tiers.map((x) => ({ ...x, label: LABEL[x.k], color: TIER_COLOR[x.k] }));
  const n = items.reduce((a, x) => a + x.n, 0);
  const pick = (x) => open(`${x.label} · market evidence`, { key: x.key });
  const foot = [skippedNote(m, targets), m.otherCountries.length ? `Other markets seen: ${m.otherCountries.map((x) => `${countryName(x.country)} (${x.posts} post${x.posts === 1 ? '' : 's'})`).join(', ')}.` : null].filter(Boolean);
  return (
    <Card span={span} title={`How much of the sample shows ${country} evidence?`} denom="Stored comments by their post’s market evidence: geotags, prices, places, scripts and similar signals, judged per post. Commenter location is not available from these platforms."
      note={n ? null : 'No comments in this slice.'} table={n ? <ShareTable head="Market evidence" rows={items} onPick={pick} extra={{ head: 'Posts', value: (x) => x.posts }} /> : null}
      foot={foot.length ? <p className="card-f small muted">{foot.join(' ')}</p> : null}>
      {n ? <Donut items={items} label="Comments by market evidence" onPick={pick} /> : null}
    </Card>
  );
}

function ContextCard({ r, d, open, span }) {
  const items = d.context.levels.map((x) => ({ ...x, label: LEVEL[x.k]?.[0] ?? x.k, color: LEVEL[x.k]?.[1] ?? OTHER }));
  const n = items.reduce((a, x) => a + x.n, 0);
  const pick = (x) => open(`${x.label} · video context`, { key: x.key });
  return (
    <Card span={span} title="What could the analysis see in each video?" denom={`Stored comments by the richest context their post had. ${r.coverage.context?.note ?? ''}`}
      note={n ? null : 'No comments in this slice.'} table={n ? <ShareTable head="Context" rows={items} onPick={pick} extra={{ head: 'Posts', value: (x) => x.posts }} /> : null}
      foot={d.context.unavailable.length ? <p className="card-f small muted">Videos not analysed: {d.context.unavailable.map((x) => `${x.posts} post${x.posts === 1 ? '' : 's'} (${gone(x.reason)})`).join('; ')}.</p> : null}>
      {n ? (
        <div className="ctx-wide">
          <Donut items={items} label="Comments by video context" onPick={pick} />
          {span === 12 ? <dl className="ctx-def">
            <dt>{LEVEL.visual[0]}</dt><dd>Frames sampled from the video were described, with any text on screen. The model’s reading of them is shown apart.</dd>
            <dt>{LEVEL.transcript[0]}</dt><dd>The platform’s own subtitles, or a transcript generated from the audio.</dd>
            <dt>{LEVEL.caption[0]}</dt><dd>Only the caption and hashtags were read, so a comment about something shown in the video may be misread.</dd>
          </dl> : null}
        </div>
      ) : null}
    </Card>
  );
}

function PublishedCard({ r, d, open, span }) {
  const tl = d.timeline, single = (color) => (b) => ({ ...b, parts: [{ k: 'n', label: '', color, n: b.n }] });
  const per = d.bucket === 'week' ? 'week' : 'day';
  const undated = tl.undated.posts || tl.undated.comments.n ? ` Undated and left out: ${nf.format(tl.undated.posts)} posts, ${nf.format(tl.undated.comments.n)} comments.` : '';
  const rows = [...new Set([...tl.posts, ...tl.comments].map((b) => b.d))].sort();
  return (
    <Card span={span} title="When were the posts and comments published?" denom={`Observed publication dates in the collected sample, per ${per}. Busier periods are not fully represented, as collection is capped.${undated}`}
      note={rows.length ? null : 'No dated records.'}
      table={rows.length ? (
        <div className="table-wrap"><table className="data">
          <thead><tr><th>{per === 'week' ? 'Week of' : 'Day'}</th><th className="num">Posts</th><th className="num">Comments</th></tr></thead>
          <tbody>{rows.map((day) => { const c = tl.comments.find((b) => b.d === day); return <tr key={day}><td>{fmtDate(day)}</td><td className="num">{nf.format(tl.posts.find((b) => b.d === day)?.n ?? 0)}</td><td className="num">{c ? <button type="button" className="linklike" onClick={() => open(`Comments · ${fmtDate(day)}`, { key: c.key })}>{nf.format(c.n)}</button> : 0}</td></tr>; })}</tbody>
        </table></div>
      ) : null}>
      {rows.length ? (
        <div className="tl-pair">
          <h4>Posts</h4>
          <TimeColumns buckets={tl.posts.map(single('var(--ink-2)'))} bucket={d.bucket} from={r.scope.since} to={r.scope.until} label={`Posts per ${per}`} />
          <h4>Comments, all stored</h4>
          <TimeColumns buckets={tl.comments.map(single('var(--c1)'))} bucket={d.bucket} from={r.scope.since} to={r.scope.until} label={`Stored comments per ${per}`} onPick={(b) => open(`Comments · ${fmtDate(b.d)}`, { key: b.key })} />
        </div>
      ) : null}
    </Card>
  );
}

function MethodCard({ r, admin, span }) {
  const name = (id) => r.scope.targets.find((x) => x.id === id)?.name ?? id;
  const c = r.coverage;
  return (
    <Card span={span} title="How was this report made, and what are its limits?">
      <dl className="cov">
        <dt>Dates</dt><dd>Searched {fmtDate(c.dates.brief.from)} to {fmtDate(c.dates.brief.to)}. Posts {c.dates.posts ? `${fmtDate(c.dates.posts.from)} to ${fmtDate(c.dates.posts.to)}` : 'undated'}; comments {c.dates.comments ? `${fmtDate(c.dates.comments.from)} to ${fmtDate(c.dates.comments.to)}` : 'undated'}.</dd>
        <dt>Comments</dt><dd>{nf.format(c.comments.stored)} stored, {nf.format(c.comments.classified)} classified, {nf.format(c.comments.analysed)} analysed for sentiment. {nf.format(c.comments.suspectedCopies)} suspected copies (kept).</dd>
        <dt>Collection jobs</dt><dd>{c.jobs.map((j, i) => <span key={i} className="tag" style={{ marginRight: 4 }}>{name(j.targetId)} · {PLATFORM_LABEL[j.platform] ?? j.platform} · {j.stage === 'comments' ? 'comments' : 'posts'}: {j.status}{j.capped ? ', capped' : ''}{j.empty ? ', empty' : ''}</span>)}</dd>
        <dt>Analysis</dt><dd>Report version {r.version}{r.reason ? ` (${r.reason})` : ''}, {fmtDate(r.createdAt)}. Model {r.model}; prompts {r.promptVersion}; context {r.contextVersion}.{admin && r.costUsd !== undefined ? ` AI cost $${r.costUsd.toFixed(4)}.` : ''}
          {r.citationCheck.length ? ` The citation check removed ${r.citationCheck.length} reference${r.citationCheck.length > 1 ? 's' : ''} to comments outside the evidence.` : ' Every citation resolved to a stored comment.'}</dd>
      </dl>
      <h4 className="limits-h">Limits of this report</h4>
      <ul className="plain-list">{r.limits.map((l, i) => <li key={i}>{l}</li>)}</ul>
    </Card>
  );
}

// ---------- the written report: findings and actions (whole sample, from the snapshot) ----------

/** Reports from report-v3 on write findings and actions per target; older ones have one list for every target. */
export const forT = (xs, t) => xs.filter((x) => !x.target || x.target === t.id);

function Findings({ r, t, open }) {
  const findings = forT(r.summary.findings, t);
  return (
    <>
      {r.summary.compareNote ? <div className="compare-note"><h3>How the targets compare</h3><p>{r.summary.compareNote}</p></div> : null}
      {findings.length ? (
        <ol className="findings">
          {findings.map((f, i) => (
            <li className="finding" key={i}>
              <span className="f-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <h3>{f.title}</h3>
              <p>{f.detail}</p>
              <Measures items={f.measures} open={open} />
              <EvidenceButtons support={f.evidence} contra={f.contradicting} open={open} title={f.title} />
            </li>
          ))}
        </ol>
      ) : <p className="muted">{enoughOf(r.targets[t.id] ?? t).ok ? `The evidence${r.scope.targets.length > 1 ? ` about ${t.name}` : ''} does not support a finding.` : `${thinText(enoughOf(r.targets[t.id] ?? t), t.name)} The Coverage tab shows what was collected and why comments were left out.`}</p>}
      {r.summary.disagreements.length ? (
        <div className="disagree"><h3>Where sources disagree</h3><ul className="plain-list">{r.summary.disagreements.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
      ) : null}
    </>
  );
}

function Actions({ r, t, open }) {
  const actions = forT(r.actions, t);
  if (!actions.length) return <p className="muted">The evidence does not support a specific action to test.</p>;
  return actions.map((a, i) => (
    <details className="action" key={i} open={i === 0}>
      <summary><strong>{a.title}</strong></summary>
      <dl className="contract">
        <dt>Observation</dt><dd>{a.observation}<Measures items={a.measures} open={open} /></dd>
        <dt>Interpretation</dt><dd>{a.interpretation}</dd>
        <dt>Principle</dt><dd><b>{a.principle.name}.</b> {a.principle.mechanism}</dd>
        <dt>Action</dt><dd>{a.action}</dd>
        <dt>How to judge it</dt><dd>{a.validationMetric}</dd>
        <dt>Limitation</dt><dd>{a.limitation}</dd>
        <dt>Evidence</dt><dd><EvidenceButtons support={a.evidence} contra={a.contradicting} open={open} title={a.title} /></dd>
      </dl>
    </details>
  ));
}

function Measures({ items, open }) {
  if (!items?.length) return null;
  return (
    <ul className="measures">
      {items.map((m) => <li key={m.key}><button className="linklike" onClick={() => open(m.text, { key: m.key })}>{m.text}</button></li>)}
    </ul>
  );
}

function EvidenceButtons({ support, contra, open, title }) {
  return (
    <div className="ev-buttons">
      {support.length ? <button className="btn small" onClick={() => open(`Supporting: ${title}`, { ids: support })}>{support.length} supporting comment{support.length > 1 ? 's' : ''}</button> : null}
      {contra.length ? <button className="btn small ghost" onClick={() => open(`Against: ${title}`, { ids: contra })}>{contra.length} contradicting</button> : null}
    </div>
  );
}

// ---------- evidence drawer ----------

/** A post's market evidence as one line: "India · likely · rupee price + Devanagari text" or "Other market · geotag Mexico". */
function MarketBadge({ m }) {
  if (!['confirmed', 'likely', 'other_market'].includes(m?.relevance)) return null;
  const why = typeof m.summary === 'string' ? m.summary.slice(0, 200) : '';
  const text = m.relevance === 'other_market'
    ? ['Other market', why || (m.otherCountry ? countryName(m.otherCountry) : '')]
    : [m.market ? countryName(m.market) : 'Chosen market', m.relevance, why];
  return <span className={`mbadge ${m.relevance}`}>{text.filter(Boolean).join(' · ')}</span>;
}

const clock = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

/** What the analysis had of the video: its transcript and source, the sampled frames, and the model's reading kept apart. */
function VideoContext({ c, tname }) {
  if (!c || (c.level === 'caption' && !c.unavailable)) return null;
  const v = c.visual, i = v?.interpretation;
  const stance = i ? Object.entries(i.stance_by_target ?? {}).map(([k, s]) => `toward ${tname(k)}: ${s}`) : [];
  return (
    <details className="vctx small">
      <summary>Video context · {LEVEL[c.level]?.[0] ?? c.level}{c.unavailable ? ' · video not analysed' : ''}</summary>
      {c.transcript ? <div className="vc-block"><h4>{c.transcript.source === 'native' ? 'The platform’s own subtitles' : 'Transcript generated from the audio'}</h4><p className="vc-text">{c.transcript.text}</p></div> : null}
      {v ? (
        <div className="vc-block">
          <h4>What the sampled frames show</h4>
          {v.summary ? <p>{v.summary}</p> : null}
          {v.frames.length ? <ol className="vc-frames">{v.frames.map((f, k) => (
            <li key={k}><span className="vc-t">{clock(f.t)}</span>{f.on_screen_text ? <span>On screen: “{f.on_screen_text}”</span> : null}{f.observed.length ? <span>{f.observed.join('; ')}</span> : null}</li>
          ))}</ol> : null}
          {v.products_seen.length ? <p>Products seen: {v.products_seen.map((x) => `${x.as_seen}${x.frames.length ? ` (at ${x.frames.map(clock).join(', ')})` : ''}`).join('; ')}</p> : null}
          {v.limitations.length ? <p className="muted">Limits: {v.limitations.join('; ')}</p> : null}
        </div>
      ) : null}
      {i ? (
        <div className="vc-block vc-model">
          <h4>The model’s interpretation, not an observation</h4>
          <p>{[i.content_type ? `Reads as ${i.content_type}` : null, i.is_promotional === null ? null : i.is_promotional ? 'promotional' : 'not promotional', ...stance, i.confidence === null ? null : `confidence ${pct(i.confidence)}`].filter(Boolean).join(' · ')}</p>
        </div>
      ) : null}
      {c.unavailable ? <p className="muted">The video itself was not analysed: {gone(c.unavailable)}.</p> : null}
    </details>
  );
}

const SENT_TAG = { positive: 'pos', negative: 'neg' };

/**
 * One thread in the drawer, the counted comments first to the eye: a counted comment in full with its counted replies, the
 * others folded; a comment shown only because a counted reply answers it becomes one faded "Replying to" line.
 */
function Thread({ c, postUrl, extra, base }) {
  const kids = c.replies ?? [], counted = kids.filter((r) => r.match), rest = kids.filter((r) => !r.match);
  const more = rest.length ? (
    <li className="more-replies"><details><summary>Show {rest.length} more {rest.length === 1 ? 'reply' : 'replies'}</summary>
      <ul className="bubbles">{rest.map((r) => <Bubble key={r.id} c={r} postUrl={postUrl} reply base={base} />)}</ul></details></li>
  ) : null;
  if (c.match) return <Bubble c={{ ...c, replies: counted }} postUrl={postUrl} extra={extra} base={base} more={more} />;
  const text = c.text.length > 160 ? `${c.text.slice(0, 159)}…` : c.text;
  return (
    <li className="thread-ctx">
      <p className="ctx-line small">Replying to <b>@{c.author ?? 'unknown'}</b>: “{text}”</p>
      <ul className="bubbles">{counted.map((r) => <Bubble key={r.id} c={r} postUrl={postUrl} reply extra={extra} base={base} />)}{more}</ul>
    </li>
  );
}

function EvidenceDrawer({ base, version, q, onClose, targets, readOnly }) {
  const [page, setPage] = useState(1);
  const [platform, setPlatform] = useState(null);
  const qs = new URLSearchParams({ ...(version ? { version: String(version) } : {}), page: String(page), ...(q.key ? { key: q.key } : { ids: q.ids.join(',') }), ...(platform ? { platform } : {}) });
  const { data, error } = useApi(`${base}/evidence?${qs}`);
  const pages = data ? Math.max(1, Math.ceil(data.totalPosts / data.pageSize)) : 1;
  const tname = (id) => targets.find((t) => t.id === id)?.name ?? id;
  const labels = (c) => c.sentiment ? (
    <span className="labels">
      <span className={`tag ${SENT_TAG[c.sentiment] ?? ''}`}>{c.sentiment}</span>
      <span className="tag">{c.target_id === 'none' ? 'not about a target' : c.target_id === 'unclear' ? 'target unclear' : tname(c.target_id)}</span>
      {c.also.map((a) => <span key={a.target} className="tag" title="This comment compares targets">{tname(a.target)}: {a.sentiment}</span>)}
      {c.voice !== 'audience' ? <span className="tag">{c.voice}</span> : null}
      {c.intent ? <span className="tag">{c.intent.replace(/_/g, ' ')}</span> : null}
      {c.spam_joke ? <span className="tag">spam or joke</span> : null}
      {c.abstain ? <span className="tag" title={c.abstain_reason}>abstained</span> : null}
      {c.themes.map((t) => <span key={t} className="tag">{t}</span>)}
      {readOnly ? null : <FlagLabel base={base} c={c} targets={targets} />}
    </span>
  ) : <span className="tag">not classified</span>;
  const first = (page - 1) * (data?.pageSize ?? 10) + 1;
  return (
    <Drawer title={q.title} onClose={onClose}>
      {error ? <div className="error-box">{error.message}</div> : !data ? <p className="muted">Loading…</p> : (
        <>
          {data.totalComments ? (
            <p className="drawer-sum"><b>{nf.format(data.totalComments)}</b> comment{data.totalComments === 1 ? '' : 's'} on <b>{nf.format(data.totalPosts)}</b> post{data.totalPosts === 1 ? '' : 's'}
              {pages > 1 ? ` · posts ${first}–${Math.min(data.totalPosts, first + data.pageSize - 1)} here, most matching comments first` : ''}.
              <span className="muted"> Original text as collected; labels are the model’s. Counted comments are marked with a blue edge.</span></p>
          ) : <p className="drawer-sum muted">No comments match this number.</p>}
          {data.platforms?.length > 1 ? <Seg label="Platform" value={platform ?? 'all'} onChange={(k) => { setPlatform(k === 'all' ? null : k); setPage(1); }}
            options={[['all', `All · ${nf.format(data.allComments)}`], ...data.platforms.map((x) => [x.p, `${PLATFORM_LABEL[x.p] ?? x.p} · ${nf.format(x.comments)}`])]} /> : null}
          {data.posts.map((p) => (
            <article className="post-card" key={p.id}>
              <div className="post-head">
                <Thumb src={p.thumbnail_url} label={PLATFORM_LABEL[p.platform]} />
                <div className="post-main">
                  <div className="qmeta small"><PlatformDot p={p.platform} /><span>@{p.creator_handle ?? 'unknown'}</span><span>{fmtDate(p.published_at)}</span>
                    {p.url ? <a href={p.url} target="_blank" rel="noreferrer noopener">Open post</a> : null}</div>
                  <MarketBadge m={{ relevance: p.market_relevance, summary: p.location_evidence?.summary, market: p.location_evidence?.market, otherCountry: p.location_evidence?.otherCountry }} />
                  <Caption text={p.caption} small platform={p.platform} />
                  {p.context ? <VideoContext c={p.context} tname={tname} /> : p.transcript ? <details className="vctx small"><summary>Video subtitles</summary><p className="vc-text">{p.transcript}</p></details> : null}
                  <span className="small muted">{p.matches} matching comment{p.matches > 1 ? 's' : ''}{p.comment_count !== null && p.comment_count !== undefined ? ` · ${compact(p.comment_count)} on the platform, of which only a sample was collected` : ''}</span>
                </div>
              </div>
              <ul className="bubbles">{p.comments.map((c) => <Thread key={c.id} c={c} postUrl={p.url} extra={labels} base={readOnly ? null : base} />)}</ul>
            </article>
          ))}
          {pages > 1 ? (
            <div className="pager">
              <button className="btn small" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
              <span className="small">Page {page} of {pages}</span>
              <button className="btn small" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  );
}

function Feedback({ base }) {
  const [f, setF] = useState({ answered: '', action: '', wrongOrMissing: '', minutesSaved: '' });
  const [state, setState] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function submit(e) {
    e.preventDefault();
    try { await api(`${base}/feedback`, { body: f }); setState('sent'); } catch (err) { setState(err.message); }
  }
  if (state === 'sent') return <section className="feedback"><h2>Thank you</h2><p className="muted">Your feedback was saved with this report version.</p></section>;
  if (!f.answered) return (
    <section className="feedback compact">
      <h2>Did this report answer your question?</h2>
      <div className="row-actions">{[['yes', 'Yes'], ['partly', 'Partly'], ['no', 'No']].map(([k, l]) => <button type="button" className="btn" key={k} onClick={() => setF({ ...f, answered: k })}>{l}</button>)}</div>
    </section>
  );
  return (
    <form className="feedback" onSubmit={submit}>
      <h2>Thanks. A few more details help us most.</h2>
      <div className="seg" role="group" aria-label="Did it answer the question">
        {[['yes', 'Yes'], ['partly', 'Partly'], ['no', 'No']].map(([k, l]) => <button type="button" key={k} aria-pressed={f.answered === k} onClick={() => setF({ ...f, answered: k })}>{l}</button>)}
      </div>
      <label className="field">What would you do based on it?<textarea value={f.action} onChange={set('action')} rows={2} /></label>
      <label className="field">What was wrong or missing?<textarea value={f.wrongOrMissing} onChange={set('wrongOrMissing')} rows={2} /></label>
      <label className="field">About how many minutes did it save you?<input type="number" min="0" value={f.minutesSaved} onChange={set('minutesSaved')} style={{ maxWidth: 140 }} /></label>
      {state ? <div className="error-box">{state}</div> : null}
      <div><button className="btn primary" disabled={!f.answered}>Send feedback</button></div>
    </form>
  );
}

const FLAG_OPTIONS = (targets) => ({
  sentiment: SENT.map((s) => [s.key, s.label]),
  target: [...targets.map((t) => [t.id, t.name]), ['none', 'Not about a target'], ['unclear', 'Unclear']],
  voice: [['audience', 'Audience'], ['creator', 'Creator'], ['brand', 'Brand']],
  spam_joke: [['true', 'Spam or joke'], ['false', 'Not spam or joke']],
});

function FlagLabel({ base, c, targets }) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState('sentiment');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [done, setDone] = useState(null);
  if (!c.classification_id) return null;
  if (done) return <span className="small muted">{done}</span>;
  if (!open) return <button className="linklike small" onClick={() => setOpen(true)}>Flag this label</button>;
  const opts = FLAG_OPTIONS(targets)[field];
  async function send() {
    try { const r = await api(`${base}/flags`, { body: { classificationId: c.classification_id, field, suggested: value, note } }); setDone(r.note); } catch (e) { setDone(e.message); }
  }
  return (
    <span className="flag-form">
      <select value={field} onChange={(e) => { setField(e.target.value); setValue(''); }} aria-label="Label to correct">
        <option value="sentiment">Sentiment</option><option value="target">About</option><option value="voice">Who wrote it</option><option value="spam_joke">Spam or joke</option>
      </select>
      <select value={value} onChange={(e) => setValue(e.target.value)} aria-label="Correct value">
        <option value="">Should be…</option>{opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input type="text" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button className="btn small" disabled={!value} onClick={send}>Send</button>
      <button className="btn small ghost" onClick={() => setOpen(false)}>Cancel</button>
    </span>
  );
}
